import {
    CirclePlus,
    ExternalLink,
    Loader2,
    Mail,
    Phone,
    RefreshCw,
    Save,
    Tag,
    Trash2,
} from 'lucide-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { ClientCallTimelineTab } from '@apps/web/components/clients/client_call_timeline_tab';
import { ClientNotificationHistoryTab } from '@apps/web/components/clients/client_notification_history_tab';
import { ClientOverview } from '@apps/web/components/clients/client_overview';
import { ClientTagOverrideChip } from '@apps/web/components/clients/client_tag_override_chip';
import { ShiftPageDetailsDialog } from '@apps/web/components/dashboard/shift_list_pane';
import { ProfileAvatar } from '@apps/web/components/entity_profile/profile_avatar';
import { ProfileShell } from '@apps/web/components/entity_profile/profile_shell';
import { EntityEventsTimeline } from '@apps/web/components/events/entity_events_timeline';
import { EntityPreferencesSection } from '@apps/web/components/memory/entity_preferences_section';
import { MemoryTab } from '@apps/web/components/memory/memory_tab';
import { useIsExtensionSurface } from '@apps/web/components/phoebe_chat/extension_surface_context';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Markdown } from '@apps/web/components/ui/markdown';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Separator } from '@apps/web/components/ui/separator';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    generateExternalClientUrl,
    getDataSourceDisplayName,
} from '@apps/web/lib/data_source_utils';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { normalizePhoneNumberToE164 } from '@apps/web/lib/phone_number_normalization';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganization,
    useOrganizationClient,
    useOrganizationDataSourceAccounts,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ClientSecondaryContact,
    type OrgClientRead,
} from '@libraries/typescript/api_client';

const US_E164_PHONE_NUMBER_PATTERN = /^\+1\d{10}$/;

// Nav display order for the profile sections; the JSX below is grouped by
// data locality, so the array is sorted into this order before rendering.
const CLIENT_SECTION_ORDER = [
    'overview',
    'care-plan',
    'contacts',
    'tags',
    'preferences',
    'calls',
    'notifications',
    'memory',
    'activity',
];

export type ClientDetailModalInitialTab =
    | 'overview'
    | 'calls'
    | 'notifications'
    | 'memory'
    | 'care-plan'
    | 'activity'
    | 'contacts'
    | 'tags'
    | 'preferences';

type ClientDetailModalProps = {
    readonly organizationId: string;
    readonly client: OrgClientRead | null;
    readonly isLoading?: boolean;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly onNavigateAdjacent?: (direction: 'previous' | 'next') => void;
    readonly onClientUpdated?: () => void;
    readonly initialTab?: ClientDetailModalInitialTab;
};

function shouldIgnoreArrowNavigation(
    target: EventTarget | null,
    profileDialog: HTMLElement | null,
): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return (
        (profileDialog !== null && !profileDialog.contains(target)) ||
        target.isContentEditable ||
        target.closest(
            'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="listbox"], [role="menu"], [role="slider"], [role="spinbutton"]',
        ) !== null
    );
}

type EditableSecondaryContact = {
    readonly id: string;
    readonly name: string;
    readonly mobile_phone_number: string;
    readonly home_phone_number: string;
    readonly is_preferred_for_client_notifications: boolean;
    readonly relation: string | null;
    readonly is_primary: boolean;
    readonly is_emergency: boolean;
    readonly is_power_of_attorney: boolean;
    readonly is_payer: boolean;
    readonly source: 'manual' | 'ehr';
    readonly notification_channel: 'call' | 'sms' | 'both';
};

/**
 * The EHR's labels for a contact, most-specific first.
 *
 * A contact commonly carries several at once, so these are independent
 * labels rather than one type. Falls back to the EHR's free-text relation
 * ("Family: Daughter") so a contact with no classification still says who
 * the person is instead of rendering as an unlabelled row.
 */
function secondaryContactLabels(
    contact: EditableSecondaryContact,
): Array<string> {
    const labels: Array<string> = [];
    if (contact.is_primary) {
        labels.push('Primary contact');
    }
    if (contact.is_emergency) {
        labels.push('Emergency contact');
    }
    if (contact.is_power_of_attorney) {
        labels.push('POA');
    }
    if (contact.is_payer) {
        labels.push('Payer');
    }
    if (labels.length === 0 && contact.relation) {
        labels.push(contact.relation);
    }
    return labels;
}

function toEditableSecondaryContacts(
    contacts: Array<ClientSecondaryContact> | null | undefined,
): Array<EditableSecondaryContact> {
    return (contacts ?? []).map((contact, index) => ({
        id: `secondary-contact-${index}`,
        name: contact.name ?? '',
        mobile_phone_number: contact.mobile_phone_number ?? '',
        home_phone_number: contact.home_phone_number ?? '',
        is_preferred_for_client_notifications:
            contact.is_preferred_for_client_notifications ?? false,
        relation: contact.relation ?? null,
        is_primary: contact.is_primary ?? false,
        is_emergency: contact.is_emergency ?? false,
        is_power_of_attorney: contact.is_power_of_attorney ?? false,
        is_payer: contact.is_payer ?? false,
        source: contact.source === 'ehr' ? 'ehr' : 'manual',
        notification_channel: contact.notification_channel ?? 'call',
    }));
}

function createEmptySecondaryContact(index: number): EditableSecondaryContact {
    return {
        id: `secondary-contact-new-${index}`,
        name: '',
        mobile_phone_number: '',
        home_phone_number: '',
        is_preferred_for_client_notifications: false,
        relation: null,
        is_primary: false,
        is_emergency: false,
        is_power_of_attorney: false,
        is_payer: false,
        source: 'manual',
        notification_channel: 'call',
    };
}

function serializeSecondaryContactsDraft(
    contacts: Array<EditableSecondaryContact>,
): string {
    return JSON.stringify(
        contacts.map((contact) => ({
            name: contact.name.trim(),
            mobile_phone_number: contact.mobile_phone_number.trim(),
            home_phone_number: contact.home_phone_number.trim(),
            is_preferred_for_client_notifications:
                contact.is_preferred_for_client_notifications,
            relation: contact.relation,
            is_primary: contact.is_primary,
            is_emergency: contact.is_emergency,
            is_power_of_attorney: contact.is_power_of_attorney,
            is_payer: contact.is_payer,
            source: contact.source,
            notification_channel: contact.notification_channel,
        })),
    );
}

function toTrimmedOrNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function composeCarePlanSummary(summary: string, bullets: string): string {
    return [summary.trim(), bullets.trim()].filter(Boolean).join('\n\n');
}

function splitCarePlanSummary(value: unknown): {
    summary: string;
    bullets: string;
} {
    if (typeof value === 'object' && value !== null) {
        const carePlanSummary = value as {
            summary?: unknown;
            bullets?: unknown;
        };
        return {
            summary:
                typeof carePlanSummary.summary === 'string'
                    ? carePlanSummary.summary
                    : '',
            bullets:
                typeof carePlanSummary.bullets === 'string'
                    ? carePlanSummary.bullets
                    : '',
        };
    }
    const carePlanSummary = typeof value === 'string' ? value.trim() : '';
    const bulletSectionMatch = carePlanSummary.match(
        /\n\s*\n(?=\s*(?:[-*]|\d+[.)])\s+)/,
    );
    if (!bulletSectionMatch || bulletSectionMatch.index === undefined) {
        return { summary: carePlanSummary, bullets: '' };
    }
    return {
        summary: carePlanSummary.slice(0, bulletSectionMatch.index).trim(),
        bullets: carePlanSummary.slice(bulletSectionMatch.index).trim(),
    };
}

function normalizeSecondaryContactPhoneNumber(value: string | null) {
    if (value === null) {
        return null;
    }
    const normalized = normalizePhoneNumberToE164(value);
    if (normalized === null || !US_E164_PHONE_NUMBER_PATTERN.test(normalized)) {
        return null;
    }
    return normalized;
}

function isValidSecondaryContactPhoneNumber(value: string | null) {
    return (
        value === null || normalizeSecondaryContactPhoneNumber(value) !== null
    );
}

type SyncClientButtonProps = {
    readonly organizationId: string;
    readonly clientId: string;
    readonly onSynced?: () => void;
};

function SyncClientButton({
    organizationId,
    clientId,
    onSynced,
}: SyncClientButtonProps) {
    const [isSyncing, setIsSyncing] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    const handleClick = useCallback(async () => {
        const controller = new AbortController();
        abortRef.current?.abort();
        abortRef.current = controller;
        setIsSyncing(true);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/clients/{client_id}/sync',
                pathParams: {
                    organization_id: organizationId,
                    client_id: clientId,
                },
                signal: controller.signal,
            });
            if (controller.signal.aborted) {
                return;
            }
            if (response.shifts_synced) {
                toast.success('Client and shifts synced', {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                });
            } else {
                toast.warning('Client synced, but not their shifts', {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                });
            }
            onSynced?.();
        } catch (error) {
            if (controller.signal.aborted) {
                return;
            }
            console.error('Failed to sync client:', error);
            toast.error('Failed to sync client. Please try again.', {
                persistNotification: true,
                notificationType: 'data_sync_action',
            });
        } finally {
            if (abortRef.current === controller) {
                abortRef.current = null;
                setIsSyncing(false);
            }
        }
    }, [organizationId, clientId, onSynced]);

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={handleClick}
            disabled={isSyncing}
            aria-busy={isSyncing}
            aria-label="Sync client data"
        >
            {isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
                <RefreshCw className="h-4 w-4" />
            )}
            {isSyncing ? 'Syncing…' : 'Sync'}
        </Button>
    );
}

export function ClientDetailModal({
    organizationId,
    client,
    isLoading = false,
    open,
    onOpenChange,
    onNavigateAdjacent,
    onClientUpdated,
    initialTab,
}: ClientDetailModalProps) {
    // In the extension panel (420px iframe) the modal stacks single-column;
    // everywhere else it keeps the original desktop two-column layout.
    const isExtensionSurface = useIsExtensionSurface();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'client-detail-modal',
    });
    const profileDialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open || !onNavigateAdjacent) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.defaultPrevented ||
                event.altKey ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey ||
                shouldIgnoreArrowNavigation(
                    event.target,
                    profileDialogRef.current,
                )
            ) {
                return;
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onNavigateAdjacent('previous');
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                onNavigateAdjacent('next');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, onNavigateAdjacent]);
    // Gates the per-tag override dropdown in the Tags section below.
    // When off, tags render as plain non-interactive badges (today's
    // behavior). Same flag controls the Tag Matching tab in settings.
    const tagOverridesEnabled = useFeatureFlag(
        'tag_overrides_enabled',
        organization?.features,
    );
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        { reason: 'client-detail-modal' },
    );
    // The clients LIST endpoint intentionally skips detail-only
    // hydration (N+1 guard), so fetch the detail record while the
    // modal is open for tag overrides and caregiver-client preferences.
    const { data: clientDetail } = useOrganizationClient(
        organizationId,
        open ? (client?.id ?? null) : null,
        { reason: 'client-detail-modal' },
    );
    const clientTagOverrides =
        clientDetail?.tag_overrides ?? client?.tag_overrides;
    const caregiverPreferenceRows = (
        clientDetail?.caregiver_client_preferences ?? []
    )
        .filter((preference) => preference.preference_value !== 'no_preference')
        .map((preference) => ({
            id: preference.id,
            name: preference.caregiver_name,
            preference_value: preference.preference_value,
            origin: preference.origin,
            source: preference.source,
        }));
    const importedPreferenceSourceLabel = clientDetail?.source_system
        ? getDataSourceDisplayName(clientDetail.source_system)
        : undefined;
    const clientNotificationEnabled =
        !!organization?.features?.client_notification_enabled;
    const [isUpdatingPhonePreference, setIsUpdatingPhonePreference] =
        useState(false);
    const [isUpdatingNotifyOnFill, setIsUpdatingNotifyOnFill] = useState(false);
    const [localNotifyOnFill, setLocalNotifyOnFill] = useState<
        boolean | null | undefined
    >(client?.default_notify_on_fill);
    const [isSavingSecondaryContacts, setIsSavingSecondaryContacts] =
        useState(false);
    const [isSavingCarePlan, setIsSavingCarePlan] = useState(false);
    const [isGeneratingCarePlan, setIsGeneratingCarePlan] = useState(false);
    const initialCarePlanDraft = splitCarePlanSummary(
        client?.care_plan_summary,
    );
    const [carePlanSummaryDraft, setCarePlanSummaryDraft] = useState(
        initialCarePlanDraft.summary,
    );
    const [savedCarePlanSummaryDraft, setSavedCarePlanSummaryDraft] = useState(
        initialCarePlanDraft.summary,
    );
    const [carePlanBulletsDraft, setCarePlanBulletsDraft] = useState(
        initialCarePlanDraft.bullets,
    );
    const [savedCarePlanBulletsDraft, setSavedCarePlanBulletsDraft] = useState(
        initialCarePlanDraft.bullets,
    );
    const [localPhonePreference, setLocalPhonePreference] = useState<
        string | null | undefined
    >(client?.client_call_phone_preference);
    const [secondaryContactsDraft, setSecondaryContactsDraft] = useState<
        Array<EditableSecondaryContact>
    >(toEditableSecondaryContacts(client?.secondary_contacts));
    const [savedSecondaryContactsDraft, setSavedSecondaryContactsDraft] =
        useState<Array<EditableSecondaryContact>>(
            toEditableSecondaryContacts(client?.secondary_contacts),
        );
    const nextSecondaryContactIdRef = useRef(0);

    useEffect(() => {
        setLocalPhonePreference(client?.client_call_phone_preference);
    }, [client?.client_call_phone_preference]);

    useEffect(() => {
        setLocalNotifyOnFill(client?.default_notify_on_fill);
    }, [client?.default_notify_on_fill]);

    useEffect(() => {
        const nextSecondaryContactsDraft = toEditableSecondaryContacts(
            client?.secondary_contacts,
        );
        setSecondaryContactsDraft(nextSecondaryContactsDraft);
        setSavedSecondaryContactsDraft(nextSecondaryContactsDraft);
    }, [client?.secondary_contacts]);

    useEffect(() => {
        nextSecondaryContactIdRef.current = 0;
    }, [client?.id]);

    useEffect(() => {
        const nextCarePlanDraft = splitCarePlanSummary(
            client?.care_plan_summary,
        );
        setCarePlanSummaryDraft(nextCarePlanDraft.summary);
        setSavedCarePlanSummaryDraft(nextCarePlanDraft.summary);
        setCarePlanBulletsDraft(nextCarePlanDraft.bullets);
        setSavedCarePlanBulletsDraft(nextCarePlanDraft.bullets);
    }, [client?.care_plan_summary]);

    const orgPhonePreference =
        organization?.general_settings?.client_call_phone_preference ??
        'mobile';
    const hasSecondaryContactsChanged = useMemo(
        () =>
            serializeSecondaryContactsDraft(secondaryContactsDraft) !==
            serializeSecondaryContactsDraft(savedSecondaryContactsDraft),
        [secondaryContactsDraft, savedSecondaryContactsDraft],
    );
    const hasCarePlanChanged =
        composeCarePlanSummary(carePlanSummaryDraft, carePlanBulletsDraft) !==
        composeCarePlanSummary(
            savedCarePlanSummaryDraft,
            savedCarePlanBulletsDraft,
        );

    const handleNotifyOnFillChange = useCallback(
        async (value: string) => {
            if (!client) {
                return;
            }
            const previousValue = localNotifyOnFill;
            const preference =
                value === 'org_default'
                    ? null
                    : value === 'always'
                      ? true
                      : false;
            setLocalNotifyOnFill(preference);
            setIsUpdatingNotifyOnFill(true);
            try {
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/clients/{client_id}',
                    pathParams: {
                        organization_id: organizationId,
                        client_id: client.id,
                    },
                    body: {
                        default_notify_on_fill: preference,
                    },
                });
                toast.success('Notify on fill preference updated', {
                    persistNotification: false,
                });
                onClientUpdated?.();
            } catch (error) {
                console.error(
                    'Failed to update notify on fill preference:',
                    error,
                );
                setLocalNotifyOnFill(previousValue);
                toast.error('Failed to update notify on fill preference', {
                    persistNotification: true,
                    notificationType: 'form_failure',
                });
            } finally {
                setIsUpdatingNotifyOnFill(false);
            }
        },
        [client, organizationId, onClientUpdated, localNotifyOnFill],
    );
    const handlePhonePreferenceChange = useCallback(
        async (value: string) => {
            if (!client) {
                return;
            }
            const previousValue = localPhonePreference;
            const preference =
                value === 'org_default' ? null : (value as 'mobile' | 'home');
            setLocalPhonePreference(preference);
            setIsUpdatingPhonePreference(true);
            try {
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/clients/{client_id}/phone-preference',
                    pathParams: {
                        organization_id: organizationId,
                        client_id: client.id,
                    },
                    body: {
                        client_call_phone_preference: preference,
                    },
                });
                toast.success('Phone preference updated', {
                    persistNotification: false,
                });
                onClientUpdated?.();
            } catch (error) {
                console.error('Failed to update phone preference:', error);
                setLocalPhonePreference(previousValue);
                toast.error('Failed to update phone preference', {
                    persistNotification: true,
                    notificationType: 'form_failure',
                });
            } finally {
                setIsUpdatingPhonePreference(false);
            }
        },
        [client, organizationId, onClientUpdated, localPhonePreference],
    );
    const handleSecondaryContactChange = useCallback(
        (
            contactId: string,
            field: 'name' | 'mobile_phone_number' | 'home_phone_number',
            value: string,
        ) => {
            setSecondaryContactsDraft((previousContacts) =>
                previousContacts.map((contact) =>
                    contact.id === contactId
                        ? { ...contact, [field]: value }
                        : contact,
                ),
            );
        },
        [],
    );
    const handlePreferredSecondaryContactChange = useCallback(
        (contactId: string, isPreferred: boolean) => {
            setSecondaryContactsDraft((previousContacts) =>
                previousContacts.map((contact) => ({
                    ...contact,
                    is_preferred_for_client_notifications:
                        contact.id === contactId ? isPreferred : false,
                })),
            );
        },
        [],
    );
    const handleAddSecondaryContact = useCallback(() => {
        setSecondaryContactsDraft((previousContacts) => {
            const nextContactId = nextSecondaryContactIdRef.current;
            nextSecondaryContactIdRef.current += 1;
            return [
                ...previousContacts,
                createEmptySecondaryContact(nextContactId),
            ];
        });
    }, []);
    const handleRemoveSecondaryContact = useCallback((contactId: string) => {
        setSecondaryContactsDraft((previousContacts) =>
            previousContacts.filter((contact) => contact.id !== contactId),
        );
    }, []);
    const handleSaveSecondaryContacts = useCallback(async () => {
        if (!client) {
            return;
        }

        const trimmedContacts = secondaryContactsDraft.map((contact) => ({
            name: toTrimmedOrNull(contact.name),
            mobile_phone_number: toTrimmedOrNull(contact.mobile_phone_number),
            home_phone_number: toTrimmedOrNull(contact.home_phone_number),
            is_preferred_for_client_notifications:
                contact.is_preferred_for_client_notifications,
            relation: contact.relation,
            is_primary: contact.is_primary,
            is_emergency: contact.is_emergency,
            is_power_of_attorney: contact.is_power_of_attorney,
            is_payer: contact.is_payer,
            source: contact.source,
            notification_channel: contact.notification_channel,
        }));
        const hasIncompleteContact = trimmedContacts.some(
            (contact) =>
                contact.name === null ||
                (contact.mobile_phone_number === null &&
                    contact.home_phone_number === null),
        );
        if (hasIncompleteContact) {
            toast.error(
                'Each secondary contact needs a name and either a mobile or home phone.',
                { persistNotification: false },
            );
            return;
        }
        const hasInvalidPhoneNumber = trimmedContacts.some(
            (contact) =>
                !isValidSecondaryContactPhoneNumber(
                    contact.mobile_phone_number,
                ) ||
                !isValidSecondaryContactPhoneNumber(contact.home_phone_number),
        );
        if (hasInvalidPhoneNumber) {
            toast.error(
                'Secondary contact phone numbers must be +1 followed by 10 digits.',
                { persistNotification: false },
            );
            return;
        }
        const normalizedContacts = trimmedContacts.map((contact) => ({
            name: contact.name!,
            mobile_phone_number: normalizeSecondaryContactPhoneNumber(
                contact.mobile_phone_number,
            ),
            home_phone_number: normalizeSecondaryContactPhoneNumber(
                contact.home_phone_number,
            ),
            is_preferred_for_client_notifications:
                contact.is_preferred_for_client_notifications,
            relation: contact.relation,
            is_primary: contact.is_primary,
            is_emergency: contact.is_emergency,
            is_power_of_attorney: contact.is_power_of_attorney,
            is_payer: contact.is_payer,
            source: contact.source,
            notification_channel: contact.notification_channel,
        }));

        setIsSavingSecondaryContacts(true);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/clients/{client_id}',
                pathParams: {
                    organization_id: organizationId,
                    client_id: client.id,
                },
                body: {
                    secondary_contacts: normalizedContacts,
                },
            });
            toast.success('Secondary contacts updated', {
                persistNotification: false,
            });
            const savedContactsDraft =
                toEditableSecondaryContacts(normalizedContacts);
            setSecondaryContactsDraft(savedContactsDraft);
            setSavedSecondaryContactsDraft(savedContactsDraft);
            onClientUpdated?.();
        } catch (error) {
            console.error('Failed to update secondary contacts:', error);
            toast.error('Failed to update secondary contacts', {
                persistNotification: true,
                notificationType: 'form_failure',
            });
        } finally {
            setIsSavingSecondaryContacts(false);
        }
    }, [client, onClientUpdated, organizationId, secondaryContactsDraft]);
    const handleSaveCarePlan = useCallback(async () => {
        if (!client) {
            return;
        }

        const carePlanSummary = carePlanSummaryDraft.trim();
        const carePlanBullets = carePlanBulletsDraft.trim();
        const carePlan =
            carePlanSummary || carePlanBullets
                ? {
                      summary: carePlanSummary,
                      bullets: carePlanBullets,
                  }
                : null;
        setIsSavingCarePlan(true);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/clients/{client_id}',
                pathParams: {
                    organization_id: organizationId,
                    client_id: client.id,
                },
                body: {
                    care_plan_summary: carePlan,
                    // User typed and clicked Save — mark this as
                    // ui-owned so the background regenerator never
                    // silently overwrites their edits.
                    care_plan_summary_source: 'ui',
                },
            });
            setCarePlanSummaryDraft(carePlanSummary);
            setSavedCarePlanSummaryDraft(carePlanSummary);
            setCarePlanBulletsDraft(carePlanBullets);
            setSavedCarePlanBulletsDraft(carePlanBullets);
            toast.success('Care plan summary updated', {
                persistNotification: false,
            });
            onClientUpdated?.();
        } catch (error) {
            console.error('Failed to update care plan summary:', error);
            toast.error('Failed to update care plan summary', {
                persistNotification: true,
                notificationType: 'form_failure',
            });
        } finally {
            setIsSavingCarePlan(false);
        }
    }, [
        carePlanBulletsDraft,
        carePlanSummaryDraft,
        client,
        onClientUpdated,
        organizationId,
    ]);
    const handleGenerateCarePlanSummary = useCallback(async () => {
        if (!client) {
            return;
        }

        setIsGeneratingCarePlan(true);
        try {
            const generatedCarePlan = await getApiClient().post({
                url: '/organizations/{organization_id}/shifts/summarize-care-plan',
                pathParams: {
                    organization_id: organizationId,
                },
                body: {
                    client_id: client.id,
                    force_generation: true,
                },
            });
            const generatedSummary = generatedCarePlan.summary?.trim() ?? '';
            const generatedBullets =
                generatedCarePlan.care_plan_bullets?.trim() ?? '';
            await getApiClient().patch({
                url: '/organizations/{organization_id}/clients/{client_id}',
                pathParams: {
                    organization_id: organizationId,
                    client_id: client.id,
                },
                body: {
                    care_plan_summary:
                        generatedSummary || generatedBullets
                            ? {
                                  summary: generatedSummary,
                                  bullets: generatedBullets,
                              }
                            : null,
                    // Verbatim LLM output the coordinator hasn't
                    // hand-edited yet — leave it eligible for
                    // background refresh on the next EHR push.
                    care_plan_summary_source: 'auto',
                },
            });
            setCarePlanSummaryDraft(generatedSummary);
            setSavedCarePlanSummaryDraft(generatedSummary);
            setCarePlanBulletsDraft(generatedBullets);
            setSavedCarePlanBulletsDraft(generatedBullets);
            toast.success('Care plan summary generated', {
                persistNotification: false,
            });
            onClientUpdated?.();
        } catch (error) {
            console.error('Failed to generate care plan summary:', error);
            toast.error('Failed to generate care plan summary', {
                persistNotification: true,
                notificationType: 'form_failure',
            });
        } finally {
            setIsGeneratingCarePlan(false);
        }
    }, [client, onClientUpdated, organizationId]);
    const resolvedInitialSection =
        initialTab === 'notifications' && !clientNotificationEnabled
            ? 'overview'
            : (initialTab ?? 'overview');
    const [activeSectionKey, setActiveSectionKey] = useState<string>(
        resolvedInitialSection,
    );
    // The modal component stays mounted across open/close cycles, so
    // re-land on the initial section whenever it (re)opens.
    useEffect(() => {
        if (open) {
            setActiveSectionKey(resolvedInitialSection);
        }
    }, [open, client?.id, resolvedInitialSection]);
    const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);

    const fullName = client ? `${client.first_name} ${client.last_name}` : '';
    const hasAddress = !!(
        client?.address_line_1 ||
        client?.city ||
        client?.state ||
        client?.postal_code
    );
    const hasTagsSection = !!(client?.tags && client.tags.length > 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* ph-no-capture: this dialog portals outside any page's
                ph-no-capture root and shows client PII, so it must opt out
                on its own. */}
            <DialogContent
                ref={profileDialogRef}
                className={
                    isExtensionSurface
                        ? 'ph-no-capture flex h-[90dvh] max-h-[90dvh] w-full max-w-none min-w-0 flex-col gap-0 overflow-hidden p-0'
                        : 'ph-no-capture flex h-[90dvh] max-h-[90dvh] w-full max-w-none min-w-0 flex-col gap-0 overflow-hidden p-0 sm:h-[min(600px,calc(100dvh-3rem))] sm:max-h-[calc(100dvh-3rem)] sm:w-[min(1180px,calc(100vw-3rem))] sm:max-w-none lg:min-w-[900px]'
                }
            >
                {isLoading ? (
                    <div className="flex h-64 items-center justify-center px-6 py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                ) : client ? (
                    <React.Fragment>
                        <DialogHeader className="shrink-0 border-b px-4 py-3 sm:px-5">
                            <div className="flex min-w-0 flex-col gap-2 pr-8 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0 space-y-1.5">
                                    <DialogTitle className="flex min-w-0 flex-wrap items-center gap-2 text-base leading-tight sm:text-lg">
                                        <ProfileAvatar
                                            entityId={client.id}
                                            displayName={fullName}
                                        />
                                        <span>{fullName}</span>
                                        {client.preferred_name && (
                                            <span className="text-muted-foreground text-sm font-normal">
                                                ({client.preferred_name})
                                            </span>
                                        )}
                                        <Badge
                                            variant={
                                                client.is_active
                                                    ? 'default'
                                                    : 'secondary'
                                            }
                                            className="text-xs"
                                        >
                                            {client.is_active
                                                ? 'Active'
                                                : 'Inactive'}
                                        </Badge>
                                    </DialogTitle>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <SyncClientButton
                                        organizationId={organizationId}
                                        clientId={client.id}
                                        onSynced={onClientUpdated}
                                    />
                                    {client.external_id &&
                                        client.source_system &&
                                        (() => {
                                            const dataSourceAccount =
                                                dataSourceAccounts?.find(
                                                    (account) =>
                                                        account.type ===
                                                        client.source_system,
                                                );

                                            if (!dataSourceAccount) {
                                                return null;
                                            }

                                            const externalUrl =
                                                generateExternalClientUrl(
                                                    client.source_system,
                                                    client.external_id,
                                                    dataSourceAccount.base_url,
                                                );
                                            const systemName =
                                                getDataSourceDisplayName(
                                                    client.source_system,
                                                );

                                            return (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    asChild={true}
                                                >
                                                    <a
                                                        href={externalUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2"
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                        View in {systemName}
                                                    </a>
                                                </Button>
                                            );
                                        })()}
                                </div>
                            </div>
                        </DialogHeader>
                        <ProfileShell
                            header={null}
                            stacked={isExtensionSurface}
                            activeSectionKey={activeSectionKey}
                            onSectionChange={setActiveSectionKey}
                            sections={[
                                {
                                    key: 'overview',
                                    label: 'Overview',
                                    content: (
                                        <ClientOverview
                                            organizationId={organizationId}
                                            client={client}
                                            timezone={
                                                organization?.timezone ?? 'UTC'
                                            }
                                            carePlanSummary={
                                                savedCarePlanSummaryDraft
                                            }
                                            carePlanBullets={
                                                savedCarePlanBulletsDraft
                                            }
                                            stacked={isExtensionSurface}
                                            onShiftClick={setSelectedShiftId}
                                        />
                                    ),
                                },
                                {
                                    key: 'contacts',
                                    label: 'Contacts',
                                    content: (
                                        <div className="max-w-4xl space-y-4">
                                            <div>
                                                <h3 className="mb-1.5 text-sm font-semibold">
                                                    Contact
                                                </h3>
                                                <div className="space-y-1">
                                                    {client.mobile_phone_number && (
                                                        <div className="flex items-center gap-2 text-sm">
                                                            <Phone className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                                            {
                                                                client.mobile_phone_number
                                                            }
                                                            <span className="text-muted-foreground text-xs">
                                                                Mobile
                                                            </span>
                                                        </div>
                                                    )}
                                                    {client.home_phone_number && (
                                                        <div className="flex items-center gap-2 text-sm">
                                                            <Phone className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                                            {
                                                                client.home_phone_number
                                                            }
                                                            <span className="text-muted-foreground text-xs">
                                                                Home
                                                            </span>
                                                        </div>
                                                    )}
                                                    {!client.mobile_phone_number &&
                                                        !client.home_phone_number &&
                                                        client.phone_number && (
                                                            <div className="flex items-center gap-2 text-sm">
                                                                <Phone className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                                                {
                                                                    client.phone_number
                                                                }
                                                            </div>
                                                        )}
                                                    {client.email && (
                                                        <div className="flex items-center gap-2 text-sm">
                                                            <Mail className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                                            {client.email}
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-0.5 text-sm">
                                                        {client.office_name && (
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-muted-foreground">
                                                                    Office
                                                                </span>
                                                                <span>
                                                                    {
                                                                        client.office_name
                                                                    }
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-muted-foreground">
                                                                Status
                                                            </span>
                                                            <Badge
                                                                variant={
                                                                    client.is_active
                                                                        ? 'default'
                                                                        : 'secondary'
                                                                }
                                                            >
                                                                {client.is_active
                                                                    ? 'Active'
                                                                    : 'Inactive'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <h3 className="mb-1.5 text-sm font-semibold">
                                                    Notification Preferences
                                                </h3>
                                                <div className="flex items-center gap-2 text-sm">
                                                    <span className="text-muted-foreground min-w-[80px]">
                                                        Call Phone:
                                                    </span>
                                                    <Select
                                                        value={
                                                            localPhonePreference ??
                                                            'org_default'
                                                        }
                                                        onValueChange={
                                                            handlePhonePreferenceChange
                                                        }
                                                        disabled={
                                                            isUpdatingPhonePreference
                                                        }
                                                    >
                                                        <SelectTrigger className="w-[200px]">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="org_default">
                                                                Org default (
                                                                {orgPhonePreference ===
                                                                'mobile'
                                                                    ? 'Mobile'
                                                                    : 'Home'}
                                                                )
                                                            </SelectItem>
                                                            <SelectItem value="mobile">
                                                                Mobile
                                                            </SelectItem>
                                                            <SelectItem value="home">
                                                                Home
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <p className="text-muted-foreground mt-1 text-xs">
                                                    Which phone number to call
                                                    when notifying this client
                                                    about shift changes.
                                                </p>
                                                <div className="mt-3 flex items-center gap-2 text-sm">
                                                    <span className="text-muted-foreground min-w-[80px]">
                                                        Notify on Fill:
                                                    </span>
                                                    <Select
                                                        value={
                                                            localNotifyOnFill ===
                                                                null ||
                                                            localNotifyOnFill ===
                                                                undefined
                                                                ? 'org_default'
                                                                : localNotifyOnFill
                                                                  ? 'always'
                                                                  : 'never'
                                                        }
                                                        onValueChange={
                                                            handleNotifyOnFillChange
                                                        }
                                                        disabled={
                                                            isUpdatingNotifyOnFill
                                                        }
                                                    >
                                                        <SelectTrigger className="w-[200px]">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="org_default">
                                                                Use org default
                                                            </SelectItem>
                                                            <SelectItem value="always">
                                                                Always notify
                                                            </SelectItem>
                                                            <SelectItem value="never">
                                                                Never notify
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <p className="text-muted-foreground mt-1 text-xs">
                                                    Whether to notify this
                                                    client by default when their
                                                    shift is filled.
                                                </p>
                                            </div>
                                            <div className="space-y-2.5">
                                                <div className="flex items-center justify-between gap-3">
                                                    <div>
                                                        <h3 className="text-sm font-semibold">
                                                            Secondary Contacts
                                                        </h3>
                                                        <p className="text-muted-foreground mt-0.5 text-xs">
                                                            People linked to
                                                            this client. Mark
                                                            one if Phoebe should
                                                            notify them instead
                                                            of the client.
                                                        </p>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={
                                                            handleAddSecondaryContact
                                                        }
                                                        disabled={
                                                            isSavingSecondaryContacts
                                                        }
                                                    >
                                                        <CirclePlus className="h-4 w-4" />
                                                        Add contact
                                                    </Button>
                                                </div>
                                                {secondaryContactsDraft.length ===
                                                0 ? (
                                                    <p className="text-muted-foreground text-sm">
                                                        No secondary contacts
                                                        saved.
                                                    </p>
                                                ) : (
                                                    <div className="space-y-4">
                                                        {secondaryContactsDraft.map(
                                                            (
                                                                contact,
                                                                index,
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        contact.id
                                                                    }
                                                                    className="space-y-3 rounded-lg border p-3"
                                                                >
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                                                                            <h4 className="text-sm font-medium">
                                                                                {contact.name.trim() ||
                                                                                    `Contact ${index + 1}`}
                                                                            </h4>
                                                                            {secondaryContactLabels(
                                                                                contact,
                                                                            ).map(
                                                                                (
                                                                                    label,
                                                                                ) => (
                                                                                    <Badge
                                                                                        key={
                                                                                            label
                                                                                        }
                                                                                        variant="secondary"
                                                                                        className="text-xs font-normal"
                                                                                    >
                                                                                        {
                                                                                            label
                                                                                        }
                                                                                    </Badge>
                                                                                ),
                                                                            )}
                                                                            {contact.source ===
                                                                                'ehr' && (
                                                                                <span className="text-muted-foreground text-xs">
                                                                                    Synced
                                                                                    from
                                                                                    WellSky
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <Button
                                                                            type="button"
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                handleRemoveSecondaryContact(
                                                                                    contact.id,
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                isSavingSecondaryContacts
                                                                            }
                                                                        >
                                                                            <Trash2 className="h-4 w-4" />
                                                                            Remove
                                                                        </Button>
                                                                    </div>
                                                                    <div className="grid gap-3 md:grid-cols-3">
                                                                        <Input
                                                                            value={
                                                                                contact.name
                                                                            }
                                                                            placeholder="Name"
                                                                            onChange={(
                                                                                event,
                                                                            ) =>
                                                                                handleSecondaryContactChange(
                                                                                    contact.id,
                                                                                    'name',
                                                                                    event
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                isSavingSecondaryContacts
                                                                            }
                                                                        />
                                                                        <Input
                                                                            value={
                                                                                contact.mobile_phone_number
                                                                            }
                                                                            placeholder="Mobile phone"
                                                                            onChange={(
                                                                                event,
                                                                            ) =>
                                                                                handleSecondaryContactChange(
                                                                                    contact.id,
                                                                                    'mobile_phone_number',
                                                                                    event
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                isSavingSecondaryContacts
                                                                            }
                                                                        />
                                                                        <Input
                                                                            value={
                                                                                contact.home_phone_number
                                                                            }
                                                                            placeholder="Home phone"
                                                                            onChange={(
                                                                                event,
                                                                            ) =>
                                                                                handleSecondaryContactChange(
                                                                                    contact.id,
                                                                                    'home_phone_number',
                                                                                    event
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                isSavingSecondaryContacts
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <label className="flex items-center gap-2 text-sm">
                                                                        <Checkbox
                                                                            checked={
                                                                                contact.is_preferred_for_client_notifications
                                                                            }
                                                                            onCheckedChange={(
                                                                                checked,
                                                                            ) =>
                                                                                handlePreferredSecondaryContactChange(
                                                                                    contact.id,
                                                                                    checked ===
                                                                                        true,
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                isSavingSecondaryContacts
                                                                            }
                                                                        />
                                                                        <span>
                                                                            Use
                                                                            for
                                                                            client
                                                                            notifications
                                                                        </span>
                                                                    </label>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                )}
                                                {hasSecondaryContactsChanged && (
                                                    <div className="flex justify-end">
                                                        <Button
                                                            type="button"
                                                            onClick={
                                                                handleSaveSecondaryContacts
                                                            }
                                                            disabled={
                                                                isSavingSecondaryContacts
                                                            }
                                                        >
                                                            <Save className="h-4 w-4" />
                                                            {isSavingSecondaryContacts
                                                                ? 'Saving…'
                                                                : 'Save contacts'}
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                            {hasAddress && (
                                                <div className="border-t pt-4">
                                                    <h3 className="mb-1.5 text-sm font-semibold">
                                                        Address
                                                    </h3>
                                                    <div className="space-y-0.5 text-sm">
                                                        {client.address_line_1 && (
                                                            <div>
                                                                {
                                                                    client.address_line_1
                                                                }
                                                            </div>
                                                        )}
                                                        {client.address_line_2 && (
                                                            <div>
                                                                {
                                                                    client.address_line_2
                                                                }
                                                            </div>
                                                        )}
                                                        {(client.city ||
                                                            client.state ||
                                                            client.postal_code) && (
                                                            <div>
                                                                {[
                                                                    client.city,
                                                                    client.state,
                                                                ]
                                                                    .filter(
                                                                        Boolean,
                                                                    )
                                                                    .join(', ')}
                                                                {client.postal_code &&
                                                                    ` ${client.postal_code}`}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ),
                                },
                                ...(hasTagsSection
                                    ? [
                                          {
                                              key: 'tags',
                                              label: 'Tags & Matching',
                                              content: (
                                                  <div className="space-y-6">
                                                      {client.tags &&
                                                          client.tags.length >
                                                              0 && (
                                                              <React.Fragment>
                                                                  <div>
                                                                      <div className="text-muted-foreground mb-2 flex items-center gap-1 text-sm">
                                                                          <Tag className="h-3 w-3" />
                                                                          Tags
                                                                          {tagOverridesEnabled && (
                                                                              <span className="text-muted-foreground ml-1 text-xs">
                                                                                  ·
                                                                                  click
                                                                                  a
                                                                                  tag
                                                                                  to
                                                                                  set
                                                                                  how
                                                                                  it
                                                                                  scores
                                                                                  caregivers
                                                                                  for
                                                                                  this
                                                                                  client
                                                                              </span>
                                                                          )}
                                                                      </div>
                                                                      <div className="flex flex-wrap gap-1">
                                                                          {client.tags.map(
                                                                              (
                                                                                  tag,
                                                                              ) =>
                                                                                  tagOverridesEnabled ? (
                                                                                      <ClientTagOverrideChip
                                                                                          key={`${client.id}:${tag}`}
                                                                                          tag={
                                                                                              tag
                                                                                          }
                                                                                          organizationId={
                                                                                              client.organization_id
                                                                                          }
                                                                                          clientId={
                                                                                              client.id
                                                                                          }
                                                                                          initialOverride={
                                                                                              clientTagOverrides?.find(
                                                                                                  (o: {
                                                                                                      tag_key: string;
                                                                                                  }) =>
                                                                                                      o.tag_key ===
                                                                                                      tag
                                                                                                          .toLowerCase()
                                                                                                          .trim(),
                                                                                              )
                                                                                                  ?.importance ??
                                                                                              'default'
                                                                                          }
                                                                                          effectiveImportance={
                                                                                              clientTagOverrides?.find(
                                                                                                  (o: {
                                                                                                      tag_key: string;
                                                                                                  }) =>
                                                                                                      o.tag_key ===
                                                                                                      tag
                                                                                                          .toLowerCase()
                                                                                                          .trim(),
                                                                                              )
                                                                                                  ?.effective_importance ??
                                                                                              null
                                                                                          }
                                                                                      />
                                                                                  ) : (
                                                                                      <Badge
                                                                                          key={
                                                                                              tag
                                                                                          }
                                                                                          variant="secondary"
                                                                                          className="text-xs"
                                                                                      >
                                                                                          {
                                                                                              tag
                                                                                          }
                                                                                      </Badge>
                                                                                  ),
                                                                          )}
                                                                      </div>
                                                                  </div>
                                                              </React.Fragment>
                                                          )}
                                                  </div>
                                              ),
                                          },
                                      ]
                                    : []),
                                {
                                    key: 'preferences',
                                    label: 'Preferences',
                                    content: (
                                        <div className="max-w-4xl space-y-4">
                                            {caregiverPreferenceRows.length >
                                            0 ? (
                                                <EntityPreferencesSection
                                                    title="Caregiver Preferences"
                                                    rows={
                                                        caregiverPreferenceRows
                                                    }
                                                    importedSourceLabel={
                                                        importedPreferenceSourceLabel
                                                    }
                                                />
                                            ) : (
                                                <p className="text-muted-foreground text-sm">
                                                    No caregiver preferences
                                                    recorded yet.
                                                </p>
                                            )}
                                        </div>
                                    ),
                                },
                                {
                                    key: 'calls',
                                    label: 'Calls',
                                    content: (
                                        <ClientCallTimelineTab
                                            organizationId={organizationId}
                                            clientId={client.id}
                                            organizationTimezone={
                                                organization?.timezone ?? null
                                            }
                                        />
                                    ),
                                },
                                {
                                    key: 'memory',
                                    label: 'Memory',
                                    content: (
                                        <MemoryTab
                                            organizationId={organizationId}
                                            entityId={client.id}
                                            entityType="client"
                                        />
                                    ),
                                },
                                {
                                    key: 'care-plan',
                                    label: 'Care Plan',
                                    content: (
                                        <div className="space-y-3">
                                            <div className="space-y-1">
                                                <div className="flex items-center justify-between gap-3">
                                                    <h3 className="text-sm font-semibold">
                                                        Care Plan Summary
                                                    </h3>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={
                                                            handleGenerateCarePlanSummary
                                                        }
                                                        disabled={
                                                            isGeneratingCarePlan ||
                                                            isSavingCarePlan ||
                                                            !client.care_plan?.trim()
                                                        }
                                                    >
                                                        <RefreshCw
                                                            className={cn(
                                                                'h-4 w-4',
                                                                isGeneratingCarePlan &&
                                                                    'animate-spin',
                                                            )}
                                                        />
                                                        {isGeneratingCarePlan
                                                            ? 'Generating…'
                                                            : 'Generate'}
                                                    </Button>
                                                </div>
                                                <p className="text-muted-foreground text-sm">
                                                    This summary will be used to
                                                    communicate care plan
                                                    details to caregivers during
                                                    outreaches
                                                </p>
                                            </div>
                                            <div className="space-y-2">
                                                <label
                                                    htmlFor={`care-plan-summary-${client.id}`}
                                                    className="text-sm font-medium"
                                                >
                                                    Summary
                                                </label>
                                                <Textarea
                                                    id={`care-plan-summary-${client.id}`}
                                                    value={carePlanSummaryDraft}
                                                    onChange={(event) =>
                                                        setCarePlanSummaryDraft(
                                                            event.target.value,
                                                        )
                                                    }
                                                    placeholder=""
                                                    className="min-h-28 resize-y"
                                                    disabled={
                                                        isSavingCarePlan ||
                                                        isGeneratingCarePlan
                                                    }
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label
                                                    htmlFor={`care-plan-bullets-${client.id}`}
                                                    className="text-sm font-medium"
                                                >
                                                    Bullets
                                                </label>
                                                <Textarea
                                                    id={`care-plan-bullets-${client.id}`}
                                                    value={carePlanBulletsDraft}
                                                    onChange={(event) =>
                                                        setCarePlanBulletsDraft(
                                                            event.target.value,
                                                        )
                                                    }
                                                    placeholder=""
                                                    className="min-h-32 resize-y"
                                                    disabled={
                                                        isSavingCarePlan ||
                                                        isGeneratingCarePlan
                                                    }
                                                />
                                            </div>
                                            {hasCarePlanChanged && (
                                                <div className="flex justify-end">
                                                    <Button
                                                        type="button"
                                                        onClick={
                                                            handleSaveCarePlan
                                                        }
                                                        disabled={
                                                            isSavingCarePlan ||
                                                            isGeneratingCarePlan
                                                        }
                                                    >
                                                        <Save className="h-4 w-4" />
                                                        {isSavingCarePlan
                                                            ? 'Saving…'
                                                            : 'Save summary'}
                                                    </Button>
                                                </div>
                                            )}
                                            <Separator />
                                            <div className="space-y-2">
                                                <h3 className="text-sm font-semibold">
                                                    Full Care Plan
                                                </h3>
                                                {client.care_plan?.trim() ? (
                                                    <Markdown
                                                        className="text-muted-foreground text-sm"
                                                        remarkPlugins={[
                                                            remarkGfm,
                                                            remarkBreaks,
                                                        ]}
                                                    >
                                                        {client.care_plan}
                                                    </Markdown>
                                                ) : (
                                                    <p className="text-muted-foreground text-sm">
                                                        No full care plan
                                                        available.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ),
                                },
                                ...(clientNotificationEnabled
                                    ? [
                                          {
                                              key: 'notifications',
                                              label: 'Notifications',
                                              content: (
                                                  <ClientNotificationHistoryTab
                                                      organizationId={
                                                          organizationId
                                                      }
                                                      clientId={client.id}
                                                      timezone={
                                                          organization?.timezone ??
                                                          null
                                                      }
                                                      onNavigateToShift={() =>
                                                          onOpenChange(false)
                                                      }
                                                  />
                                              ),
                                          },
                                      ]
                                    : []),
                                {
                                    key: 'activity',
                                    label: 'Activity',
                                    content: (
                                        <EntityEventsTimeline
                                            organizationId={organizationId}
                                            entityType="client"
                                            entityId={client.id}
                                        />
                                    ),
                                },
                            ].sort(
                                (a, b) =>
                                    CLIENT_SECTION_ORDER.indexOf(a.key) -
                                    CLIENT_SECTION_ORDER.indexOf(b.key),
                            )}
                        />
                        <ShiftPageDetailsDialog
                            organizationId={organizationId}
                            shiftId={selectedShiftId}
                            isOpen={selectedShiftId !== null}
                            onClose={() => setSelectedShiftId(null)}
                        />
                    </React.Fragment>
                ) : (
                    <div className="py-8 text-center">
                        <p className="text-muted-foreground">
                            Client not found
                        </p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
